// src/components/TailorRecommendationsPreview.tsx
import React, { useState } from "react";
import "../styles/TailorRecommendationsPreview.css";

interface TailorRecommendationsPreviewProps {
  tailoringData: any;
  onClose: () => void;
  onApplySuggestions: (suggestions: string[]) => void;
}

const TailorRecommendationsPreview: React.FC<TailorRecommendationsPreviewProps> = ({
  tailoringData,
  onClose,
  onApplySuggestions,
}) => {
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<"overview" | "actions" | "skills" | "experience">(
    "overview"
  );

  const toggleAction = (action: string) => {
    setSelectedActions((prev) =>
      prev.includes(action) ? prev.filter((a) => a !== action) : [...prev, action]
    );
  };

  const getMatchColor = (percentage: number) => {
    if (percentage >= 80) return "#4caf50";
    if (percentage >= 60) return "#ff9800";
    return "#f44336";
  };

  const getMatchLabel = (percentage: number) => {
    if (percentage >= 80) return "Excellent Match";
    if (percentage >= 60) return "Good Match";
    if (percentage >= 40) return "Partial Match";
    return "Skills Gap";
  };

  return (
    <div className="trp-overlay">
      <div className="trp-modal">
        <div className="trp-header">
          <div className="trp-title">
            <h2>Resume Tailoring Recommendations</h2>
            <p className="trp-job-info">
              {tailoringData.jobTitle} at {tailoringData.companyName}
            </p>
          </div>
          <button className="trp-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Match Score */}
        <div className="trp-match-banner">
          <div className="trp-match-score">
            <div
              className="trp-match-circle"
              style={{ borderColor: getMatchColor(tailoringData.overallMatch) }}
            >
              <span>{tailoringData.overallMatch}%</span>
            </div>
            <div className="trp-match-text">
              <p className="trp-match-label">{getMatchLabel(tailoringData.overallMatch)}</p>
              <p className="trp-match-detail">
                Your resume matches {tailoringData.overallMatch}% of the job requirements
              </p>
            </div>
          </div>
          <div className="trp-ats-info">
            <div className="trp-ats-item">
              <span className="trp-label">Current ATS Score</span>
              <span className="trp-value">{tailoringData.atsScore.current}</span>
            </div>
            <div className="trp-ats-item">
              <span className="trp-label">Potential After Tailoring</span>
              <span className="trp-value trp-improvement">
                {tailoringData.atsScore.potentialScore}
                <span className="trp-improvement-badge">
                  +{tailoringData.atsScore.improvement}
                </span>
              </span>
            </div>
            <div className="trp-ats-item">
              <span className="trp-label">Estimated Time to Tailor</span>
              <span className="trp-value">{tailoringData.estimatedTimeToTailor}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="trp-tabs">
          <button
            className={`trp-tab ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            📋 Overview
          </button>
          <button
            className={`trp-tab ${activeTab === "actions" ? "active" : ""}`}
            onClick={() => setActiveTab("actions")}
          >
            🎯 Priority Actions
          </button>
          <button
            className={`trp-tab ${activeTab === "skills" ? "active" : ""}`}
            onClick={() => setActiveTab("skills")}
          >
            🛠️ Skills Gap
          </button>
          <button
            className={`trp-tab ${activeTab === "experience" ? "active" : ""}`}
            onClick={() => setActiveTab("experience")}
          >
            💼 Experience
          </button>
        </div>

        {/* Content */}
        <div className="trp-content">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="trp-section">
              <h3>Jobs Requirements vs Your Resume</h3>

              {/* AI-tailored summary (shown only when LLM produced one) */}
              {tailoringData.llmSummary && (
                <div className="trp-card" style={{ borderLeft: "3px solid #2563eb" }}>
                  <div className="trp-card-header">
                    <h4>🤖 AI Tailored Summary</h4>
                  </div>
                  <p style={{ margin: "8px 0 4px", lineHeight: 1.5, color: "#1f2937" }}>
                    {tailoringData.llmSummary}
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
                    Drop this near the top of your resume as a personalised pitch for this role.
                  </p>
                </div>
              )}

              {/* Matched Skills */}
              {tailoringData.skillGap.matched.length > 0 && (
                <div className="trp-card trp-matched">
                  <div className="trp-card-header">
                    <h4>✅ Matched Skills ({tailoringData.skillGap.matched.length})</h4>
                  </div>
                  <div className="trp-skills-grid">
                    {tailoringData.skillGap.matched.map((skill: string, idx: number) => (
                      <span key={idx} className="trp-skill-tag">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Missing Skills */}
              {tailoringData.skillGap.missing.length > 0 && (
                <div className="trp-card trp-missing">
                  <div className="trp-card-header">
                    <h4>🔴 Missing Skills ({tailoringData.skillGap.missing.length})</h4>
                    <p className="trp-card-note">Consider highlighting similar skills or experience</p>
                  </div>
                  <div className="trp-skills-grid">
                    {tailoringData.skillGap.missing.map((skill: string, idx: number) => (
                      <span key={idx} className="trp-skill-tag trp-missing-tag">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience Match */}
              <div className="trp-card">
                <div className="trp-card-header">
                  <h4>📅 Experience Level</h4>
                </div>
                <div className="trp-experience-info">
                  <div className="trp-exp-item">
                    <span className="trp-label">Your Experience</span>
                    <span className="trp-value">{tailoringData.experienceMatch.yourExperience} years</span>
                  </div>
                  <div className="trp-exp-item">
                    <span className="trp-label">Required Experience</span>
                    <span className="trp-value">{tailoringData.experienceMatch.required} years</span>
                  </div>
                  <div className="trp-exp-message">
                    {tailoringData.experienceMatch.meetsRequirement ? (
                      <span className="trp-check">✓ {tailoringData.experienceMatch.message}</span>
                    ) : (
                      <span className="trp-warning">⚠ {tailoringData.experienceMatch.message}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Priority Actions Tab */}
          {activeTab === "actions" && (
            <div className="trp-section">
              <h3>Recommended Actions</h3>
              <div className="trp-actions-list">
                {tailoringData.priorityActions.map((action: string, idx: number) => (
                  <div key={idx} className="trp-action-item">
                    <input
                      type="checkbox"
                      id={`action-${idx}`}
                      checked={selectedActions.includes(action)}
                      onChange={() => toggleAction(action)}
                    />
                    <label htmlFor={`action-${idx}`}>{action}</label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Skills Tab */}
          {activeTab === "skills" && (
            <div className="trp-section">
              <h3>Skills Recommendations</h3>
              {Object.entries(tailoringData.tailoringRecommendations).map(
                ([sectionKey, recommendations]: [string, any]) => {
                  if (sectionKey !== "skills_section" || !recommendations) return null;
                  return (
                    <div key={sectionKey}>
                      {Array.isArray(recommendations) &&
                        recommendations.map((rec: any, idx: number) => (
                          <div key={idx} className="trp-recommendation">
                            <div className="trp-rec-header">
                              <span className={`trp-priority ${rec.priority}`}>
                                {rec.priority.toUpperCase()}
                              </span>
                              <p>{rec.suggestion}</p>
                            </div>
                            {rec.action && <p className="trp-action">{rec.action}</p>}
                            {rec.estimatedTime && (
                              <p className="trp-time">⏱️ {rec.estimatedTime}</p>
                            )}
                          </div>
                        ))}
                    </div>
                  );
                }
              )}
            </div>
          )}

          {/* Experience Tab */}
          {activeTab === "experience" && (
            <div className="trp-section">
              <h3>Experience Section Recommendations</h3>
              {Object.entries(tailoringData.tailoringRecommendations).map(
                ([sectionKey, recommendations]: [string, any]) => {
                  if (sectionKey !== "experience_section" || !recommendations) return null;
                  return (
                    <div key={sectionKey}>
                      {Array.isArray(recommendations) &&
                        recommendations.map((rec: any, idx: number) => (
                          <div key={idx} className="trp-recommendation">
                            <div className="trp-rec-header">
                              <span className={`trp-priority ${rec.priority}`}>
                                {rec.priority.toUpperCase()}
                              </span>
                              <p>{rec.suggestion}</p>
                            </div>
                            {rec.example && (
                              <p className="trp-example">
                                <strong>Example:</strong> {rec.example}
                              </p>
                            )}
                            {rec.action && <p className="trp-action">{rec.action}</p>}
                            {rec.estimatedTime && (
                              <p className="trp-time">⏱️ {rec.estimatedTime}</p>
                            )}
                          </div>
                        ))}
                    </div>
                  );
                }
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="trp-footer">
          <button className="trp-btn-close" onClick={onClose}>
            Close
          </button>
          <button
            className="trp-btn-apply"
            onClick={() => {
              onApplySuggestions(selectedActions);
              onClose();
            }}
            disabled={selectedActions.length === 0}
          >
            💾 Apply Selected Actions ({selectedActions.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default TailorRecommendationsPreview;
